import { mdiClose } from '@mdi/js';
import { Icon } from '@mdi/react';
import { Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import { FC } from 'react';
import { FormattedMessage } from 'react-intl';

import { defineStyles } from '../../theme';

const styles = defineStyles({
  root: {
    height: '100%',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 2,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
    pt: 2,
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0.5,
    pl: 2,
  },
  listItem: {
    display: 'flex',
    gap: 1,
  },
  actions: {
    px: 3,
    pb: 2,
    gap: 2,
  },
});

export type ResponseCodeCounts = Readonly<Record<string, number>>;

export type DeleteSampleConfirmDialogProps = {
  open: boolean;
  responseCodeCounts: ResponseCodeCounts;
  onCancel: () => void;
  onConfirm: () => void;
};

export const DeleteSampleConfirmDialog: FC<DeleteSampleConfirmDialogProps> = ({
  open,
  responseCodeCounts,
  onCancel,
  onConfirm,
}) => {
  const entries = Object.entries(responseCodeCounts)
    .map(([code, count]) => ({ code: Number(code), count }))
    .filter(({ count }) => count > 0)
    .sort((a, b) => a.code - b.code);

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth sx={styles.root}>
      <DialogTitle sx={styles.header}>
        <FormattedMessage
          id="deleteSampleConfirm.title"
          defaultMessage="Warning: you're deleting completed survey rows"
        />
        <ButtonBase onClick={onCancel} disableRipple>
          <Icon path={mdiClose} size={1} />
        </ButtonBase>
      </DialogTitle>
      <DialogContent sx={styles.content}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          <FormattedMessage
            id="deleteSampleConfirm.description"
            defaultMessage="Some of the selected rows have a response code indicating the survey has been completed. Do you really want to delete them?"
          />
        </Typography>
        <Box component="ul" sx={styles.list}>
          {entries.map(({ code, count }) => (
            <Box component="li" key={code} sx={styles.listItem}>
              <Typography variant="body2">
                <FormattedMessage
                  id="deleteSampleConfirm.responseCodeRow"
                  defaultMessage="Response code {code}: {count, plural, one {# row} other {# rows}}"
                  values={{ code, count }}
                />
              </Typography>
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions sx={styles.actions}>
        <Button onClick={onCancel} variant="outlined">
          <FormattedMessage id="common.cancel.button" defaultMessage="Cancel" />
        </Button>
        <Button onClick={onConfirm} color="error" variant="contained">
          <FormattedMessage id="deleteSampleConfirm.confirmButton" defaultMessage="Delete anyway" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};
